import React, { useState, useEffect, useCallback } from "react";
import { useCart } from "./Cart";
import Footer from "./Footer";
import { motion } from "framer-motion";
import ProductCard from "./components/ProductCard";
import { useNavigate, useLocation } from "react-router-dom";
import {
  filterHiddenCategories,
  filterHiddenCategoriesFromList,
  getProductImage,
} from "./utils/categoryUtils";

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Cache for all products
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [dataFetched, setDataFetched] = useState(false); // Track if we've fetched all data
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse category from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      // Find the actual category name from categories list (case-insensitive match)
      const matchedCategory = categories.find(c => 
        c.toLowerCase() === categoryParam.toLowerCase()
      );
      if (matchedCategory) {
        setSelectedCategory(matchedCategory);
      } else if (categoryParam.toLowerCase() === 'supplement' || categoryParam.toLowerCase() === 'supplements') {
        // Handle specific case for supplements as requested
        const supplementCat = categories.find(c => 
          c.toLowerCase().includes('supplement')
        );
        if (supplementCat) setSelectedCategory(supplementCat);
      }
    }
  }, [location.search, categories]);

  // Responsive items per page based on screen size (max 20 products per page)
  const getItemsPerPage = () => {
    if (typeof window !== "undefined") {
      if (window.innerWidth < 640) return 8; // Mobile: 2 columns
      if (window.innerWidth < 768) return 12; // Small tablet: 3 columns
      if (window.innerWidth < 1024) return 16; // Tablet: 4 columns
      return 20; // Desktop and above: 5 columns (max 20)
    }
    return 12;
  };

  const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage());

  const defaultImage =
    "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.0.3";

  // Handle responsive items per page
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(getItemsPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedCategory, itemsPerPage]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        console.log("Fetching categories...");
        const categoriesResponse = await fetch(
          "https://dawamaxma.dawamtaani.co.ke/api/pharmacy/categories"
        );
        if (!categoriesResponse.ok) {
          throw new Error(
            `Categories API failed: ${categoriesResponse.status}`
          );
        }
        const categoriesData = await categoriesResponse.json();
        console.log("Categories fetched:", categoriesData);
        // Filter out hidden categories (check both category and as potential subcategory)
        const filteredCategories =
          filterHiddenCategoriesFromList(categoriesData);
        console.log(
          "Categories after filtering hidden ones:",
          filteredCategories
        );
        setCategories(["All", ...filteredCategories]);
      } catch (error) {
        console.error("Error fetching categories:", error);
        // Set a default category list as fallback
        setCategories([
          "All",
          "Medicine",
          "Supplements",
          "Personal Care",
          "Baby Care",
        ]);
        console.log("Using fallback categories");
      }
    };

    fetchCategories();
  }, []);

  // Fetch products with pagination and filtering
  useEffect(() => {
    const fetchProducts = async () => {
      // If we already have all data cached, use client-side filtering and pagination
      if (dataFetched && allProducts.length > 0) {
        console.log(
          "Using cached data for client-side filtering and pagination"
        );
        applyClientSidePagination();
        return;
      }

      // Show pagination loading for page changes, full loading for initial load
      if (currentPage === 1 && !dataFetched) {
        setLoading(true);
      } else {
        setPaginationLoading(true);
      }

      try {
        console.log("Fetching products...", {
          currentPage,
          selectedCategory,
          debouncedSearchTerm,
          itemsPerPage,
        });

        // For the first request, try to get all data since the API returns everything anyway
        const url = `https://dawamaxma.dawamtaani.co.ke/api/pharmacy`;
        console.log("Fetching from URL:", url);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Products API failed: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        console.log("Products response:", data);
        console.log("Response type:", Array.isArray(data) ? "array" : "object");
        console.log(
          "Data length/count:",
          Array.isArray(data)
            ? data.length
            : data.data
            ? data.data.length
            : "unknown"
        );
        console.log("Has pagination:", !!data.pagination);

        // Handle API response format
        if (data.data && Array.isArray(data.data) && data.pagination) {
          // New format with pagination - this is what we expect
          console.log("Using paginated API response:", data);

          // Filter out hidden categories from paginated response
          const filteredPaginatedData = filterHiddenCategories(data.data);
          console.log(
            "Paginated products after filtering hidden categories:",
            filteredPaginatedData.length,
            "out of",
            data.data.length
          );

          // Cache filtered data for client-side filtering
          setAllProducts(filteredPaginatedData);
          setDataFetched(true);

          // Apply client-side pagination immediately
          applyClientSidePagination(filteredPaginatedData);
        } else if (Array.isArray(data)) {
          // API returned all data as array, cache it and apply client-side pagination
          console.warn(
            "API returned all data as array. Caching and implementing client-side pagination. Total items:",
            data.length
          );

          // Filter out hidden categories before caching
          const filteredData = filterHiddenCategories(data);
          console.log(
            "Products after filtering hidden categories:",
            filteredData.length
          );

          // Cache all products for future use
          setAllProducts(filteredData);
          setDataFetched(true);

          // Apply client-side pagination immediately
          applyClientSidePagination(filteredData);
        } else {
          console.error("Unexpected response format:", data);
          throw new Error("Unexpected response format from server");
        }

        setError(null); // Clear any previous errors
      } catch (error) {
        console.error("Error fetching products:", error);

        // Only show fallback data on initial load, not pagination errors
        if (currentPage === 1) {
          console.log("Using fallback mock data for products...");
          const mockProducts = [
            {
              id: 1,
              itemName: "Paracetamol 500mg",
              retailPrice: "150",
              category: "Medicine",
              image: null,
              Prescription: "FALSE",
            },
            {
              id: 2,
              itemName: "Vitamin C Tablets",
              retailPrice: "300",
              category: "Supplements",
              image: null,
              Prescription: "FALSE",
            },
            {
              id: 3,
              itemName: "Hand Sanitizer",
              retailPrice: "200",
              category: "Personal Care",
              image: null,
              Prescription: "FALSE",
            },
            {
              id: 4,
              itemName: "Antibiotics (Prescription)",
              retailPrice: "500",
              category: "Medicine",
              image: null,
              Prescription: "TRUE",
            },
            {
              id: 5,
              itemName: "Baby Formula",
              retailPrice: "800",
              category: "Baby Care",
              image: null,
              Prescription: "FALSE",
            },
          ];

          // Filter by category and search if selected
          let filteredProducts = mockProducts;
          if (selectedCategory && selectedCategory !== "All") {
            filteredProducts = filteredProducts.filter(
              (product) => product.category === selectedCategory
            );
          }
          if (debouncedSearchTerm) {
            filteredProducts = filteredProducts.filter((product) =>
              product.itemName
                .toLowerCase()
                .includes(debouncedSearchTerm.toLowerCase())
            );
          }

          setProducts(filteredProducts);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalItems: filteredProducts.length,
            itemsPerPage: filteredProducts.length,
          });
          setError(
            `API temporarily unavailable. Showing sample data. Error: ${error.message}`
          );
        } else {
          // For pagination errors, just show error without changing products
          setError(`Failed to load page ${currentPage}. Please try again.`);
        }
      } finally {
        setLoading(false);
        setPaginationLoading(false);
      }
    };

    fetchProducts();
  }, [
    currentPage,
    selectedCategory,
    debouncedSearchTerm,
    itemsPerPage,
    dataFetched,
    allProducts,
  ]);

  // Separate effect for client-side pagination when data is already cached
  useEffect(() => {
    if (dataFetched && allProducts.length > 0) {
      applyClientSidePagination();
    }
  }, [
    currentPage,
    selectedCategory,
    debouncedSearchTerm,
    itemsPerPage,
    dataFetched,
    allProducts,
  ]);

  // Client-side pagination function
  const applyClientSidePagination = (dataToUse = allProducts) => {
    console.log("Applying client-side pagination with:", {
      dataLength: dataToUse.length,
      currentPage,
      selectedCategory,
      debouncedSearchTerm,
      itemsPerPage,
    });

    // Apply filters client-side
    let filteredData = dataToUse;

    // Filter out hidden categories (should already be filtered, but double-check)
    filteredData = filterHiddenCategories(filteredData);

    // Filter by category
    if (selectedCategory && selectedCategory !== "All") {
      filteredData = filteredData.filter(
        (product) => product.category === selectedCategory
      );
      console.log(
        "Filtered by category:",
        selectedCategory,
        "Results:",
        filteredData.length
      );
    }

    // Filter by search term
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filteredData = filteredData.filter(
        (product) =>
          product.itemName?.toLowerCase().includes(searchLower) ||
          product.category?.toLowerCase().includes(searchLower) ||
          product.sub_category?.toLowerCase().includes(searchLower) ||
          product.brand?.toLowerCase().includes(searchLower) ||
          product.description?.toLowerCase().includes(searchLower)
      );
      console.log(
        "Filtered by search:",
        debouncedSearchTerm,
        "Results:",
        filteredData.length
      );
    }

    // Apply pagination to filtered data
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setProducts(paginatedData);
    setPagination({
      currentPage: currentPage,
      totalPages: Math.ceil(filteredData.length / itemsPerPage),
      totalItems: filteredData.length,
      itemsPerPage: itemsPerPage,
    });

    console.log("Client-side pagination applied:", {
      totalFiltered: filteredData.length,
      currentPage: currentPage,
      itemsPerPage: itemsPerPage,
      showing: paginatedData.length,
    });
  };

  // All filtering and pagination is handled client-side due to API limitations
  const displayProducts = products;
  const totalPages = pagination.totalPages;

  // Handle prescription products - memoized to prevent re-renders
  const handleAddToCart = useCallback(
    (product) => {
      const isPrescriptionRequired =
        product.Prescription === "TRUE" ||
        product.Prescription === true ||
        product.Prescription === 1;
      if (isPrescriptionRequired) {
        setPendingProduct(product);
        setShowPrescriptionModal(true);
      } else {
        addToCart(product);
      }
    },
    [addToCart]
  );

  const handlePrescriptionUpload = () => {
    if (pendingProduct) {
      addToCart(pendingProduct);
      setShowPrescriptionModal(false);
      setPendingProduct(null);
    }
  };

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50 flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-neutral-700 mb-2">
            Loading Products
          </h2>
          <p className="text-neutral-500">
            Please wait while we fetch the latest products...
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50 flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-12 h-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-neutral-700 mb-4">
            Failed to load products
          </h2>
          <p className="text-red-600 mb-6 max-w-md mx-auto">{error}</p>
          <motion.button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50">
      <motion.div
        className="container mx-auto px-3 sm:px-4 py-8 sm:py-12 lg:py-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.0 }}
      >
        {/* Header Section - Responsive */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 sm:mb-4 px-4 sm:px-0">
              Shop All{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                Products
              </span>
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-neutral-600 max-w-2xl mx-auto mb-6 sm:mb-8 px-4 sm:px-0">
              Browse our comprehensive collection of healthcare products and
              medications
            </p>
          </motion.div>

          {/* Search Bar - Responsive */}
          <motion.div
            className="flex justify-center mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative w-full max-w-2xl px-4 sm:px-0">
              <div className="absolute inset-y-0 left-4 sm:left-6 pl-0 sm:pl-0 flex items-center pointer-events-none">
                <svg
                  className="h-4 sm:h-5 w-4 sm:w-5 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search medications, supplements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 sm:py-4 pl-10 sm:pl-14 pr-4 sm:pr-6 bg-white border-2 border-neutral-200 rounded-xl sm:rounded-2xl shadow-soft focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-lg placeholder-neutral-400 transition-all duration-300"
              />
            </div>
          </motion.div>

          {/* Category Filter - Responsive */}
          <motion.div
            className="flex justify-center px-4 sm:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center max-w-4xl">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() =>
                    setSelectedCategory(category === "All" ? "" : category)
                  }
                  className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                    selectedCategory === category ||
                    (selectedCategory === "" && category === "All")
                      ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-medium"
                      : "bg-white text-neutral-600 border-2 border-neutral-200 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Results Info */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
          <p className="text-neutral-600 text-sm sm:text-base">
            {debouncedSearchTerm
              ? `Found ${pagination.totalItems} products matching "${debouncedSearchTerm}"`
              : `Showing ${displayProducts.length} of ${
                  pagination.totalItems
                } products${
                  selectedCategory && selectedCategory !== "All"
                    ? ` in ${selectedCategory}`
                    : ""
                }`}
          </p>
          {totalPages > 1 && (
            <p className="text-xs sm:text-sm text-neutral-500">
              Page {pagination.currentPage} of {totalPages}
            </p>
          )}
        </div>

        {/* Products Grid - Responsive layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-8 min-h-[400px]">
          {loading || paginationLoading ? (
            // Loading skeleton - responsive
            [...Array(itemsPerPage)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl shadow-soft border border-neutral-100 overflow-hidden animate-pulse"
              >
                <div className="w-full aspect-square bg-neutral-200"></div>
                <div className="p-4 lg:p-6">
                  <div className="h-4 bg-neutral-200 rounded mb-2"></div>
                  <div className="h-4 bg-neutral-200 rounded w-3/4 mb-4"></div>
                  <div className="h-10 bg-neutral-200 rounded-2xl"></div>
                </div>
              </div>
            ))
          ) : displayProducts.length > 0 ? (
            displayProducts.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                defaultImage={defaultImage}
              />
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-16">
              <div className="w-24 h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-12 h-12 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-neutral-700 mb-2">
                No products found
              </h3>
              <p className="text-neutral-500 text-center max-w-md">
                We couldn't find any products matching your search. Try
                adjusting your search terms.
              </p>
            </div>
          )}
        </div>

        {/* Pagination - Responsive */}
        {totalPages > 1 && (
          <motion.div
            className="flex justify-center items-center space-x-1 sm:space-x-2 mt-8 sm:mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || paginationLoading}
              className="px-2 sm:px-4 py-2 rounded-lg sm:rounded-xl bg-white border-2 border-neutral-200 text-neutral-600 hover:border-primary-300 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-sm sm:text-base"
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">‹</span>
            </button>

            {/* Mobile: Show only current page and total */}
            <div className="sm:hidden flex items-center space-x-2">
              <span className="px-3 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg font-semibold text-sm">
                {pagination.currentPage}
              </span>
              <span className="text-neutral-400 text-sm">of {totalPages}</span>
            </div>

            {/* Desktop: Show page numbers */}
            <div className="hidden sm:flex items-center space-x-2">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                const isCurrentPage = page === pagination.currentPage;
                const showPage =
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1);

                if (!showPage) {
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="px-2 text-neutral-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    disabled={paginationLoading}
                    className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                      isCurrentPage
                        ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-medium"
                        : "bg-white border-2 border-neutral-200 text-neutral-600 hover:border-primary-300 hover:text-primary-600 disabled:opacity-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || paginationLoading}
              className="px-2 sm:px-4 py-2 rounded-lg sm:rounded-xl bg-white border-2 border-neutral-200 text-neutral-600 hover:border-primary-300 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-sm sm:text-base"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">›</span>
            </button>
          </motion.div>
        )}
      </motion.div>

      {/* Prescription Upload Modal - Responsive */}
      {showPrescriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <motion.div
            className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 max-w-md w-full mx-3 sm:mx-0"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-neutral-900 mb-3 sm:mb-4">
              Prescription Required
            </h3>
            <p className="text-sm sm:text-base text-neutral-600 mb-4 sm:mb-6">
              This product requires a prescription. Please upload your
              prescription to add it to your cart.
            </p>

            <div className="mb-4 sm:mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Upload Prescription
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
              />
            </div>

            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => setShowPrescriptionModal(false)}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-neutral-100 text-neutral-700 rounded-lg sm:rounded-xl font-semibold hover:bg-neutral-200 transition-colors duration-300 text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={handlePrescriptionUpload}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg sm:rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300 text-sm sm:text-base"
              >
                Add to Cart
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Shop;
